import React, { useEffect, useState } from 'react'
import UpgradePlanPopup from './UpgradePlan'
import CancellationPlans from './CancellationPlans'
import FeedbackPopup from './FeedbackPopup'
import { Box, Button, Card, Chip, CircularProgress, Divider, Grid, Typography, useTheme, TextField, Select, MenuItem, FormControl, FormHelperText } from '@mui/material'
import { useGetPaymentMethodQuery, useInvoicesQuery, useUpdatePaymentMutation } from 'src/services/billing-service'
import { useDispatch, useSelector } from 'react-redux'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import moment from 'moment-timezone'
import { Link } from 'react-router-dom'
import { setUser, useUpdateCompanyInfoMutation } from 'src/services/user-service'
import { countryPhoneCodes } from 'src/utils/countriesData'
import { toast } from 'react-hot-toast'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'

// Validation schema for the billing form
const BillingDetailsSchema = Yup.object().shape({
  companyName: Yup.string().trim(),
  companyAddress: Yup.string().trim(),
  selectedCountryCode: Yup.string().required('Country code is required'),
  phoneNumber: Yup.string().trim()
    .matches(/^[0-9]*$/, 'Phone number must contain only digits')
})

const BillingUpdatedPage = ({ setValue, canEdit }) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const user = useSelector((state) => state.user)
  const workspace = useSelector((state) => state.workspace)
  const [workspaceId, setWorkspaceId] = useState(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [cancellationPlansOpen, setCancellationPlansOpen] = useState(false)
  const [feedbackPopupOpen, setFeedbackPopupOpen] = useState(false)

  const { data, isLoading: isCardLoading } = useGetPaymentMethodQuery()
  const [UpdatePayment] = useUpdatePaymentMutation()
  const latestPaymentMethod = Array.isArray(data) && data.length > 0 ? [...data].sort((a, b) => b.id.localeCompare(a.id))[0] : null
  const isCardExpired = (expMonth, expYear) => {
    const cardExpiry = moment(`${expYear}-${expMonth}`, 'YYYY-M').endOf('month')
    return moment().isAfter(cardExpiry)
  }
  const { data: invoices = [], isLoading, refetch: refetchInvoice } = useInvoicesQuery()
  const [isDownloading, setIsDownloading] = useState(false)
  const [displayCount, setDisplayCount] = useState(4)
  const visibleInvoices = invoices?.slice(0, displayCount) || []
  const remainingCount = (invoices?.length || 0) - displayCount
  const hasMoreInvoices = remainingCount > 0
  const [billingFormData, setBillingFormData] = useState({
    companyName: '',
    companyAddress: '',
    selectedCountryCode: '+1',
    phoneNumber: '' // This will store the phone WITHOUT country code
  })
  const [updateCompanyInfo, { isLoading: isUpdatingCompanyInfo }] = useUpdateCompanyInfoMutation()

  // Initialize form with user data when loaded
  useEffect(() => {
    if (user) {
      // Parse phone number to separate country code if it exists
      let phoneNumber = user.phoneNumber || ''
      let selectedCountryCode = '+1'

      // Extract country code from phone number
      if (phoneNumber) {
        // Find the country code at the beginning of the phone number
        const countryCodeObj = countryPhoneCodes.find(cc => phoneNumber.startsWith(cc.dial_code))
        // If a country code is found, set it as the selected country code
        if (countryCodeObj) {
          selectedCountryCode = countryCodeObj.dial_code
          // Remove country code from phone number
          phoneNumber = phoneNumber.substring(countryCodeObj.dial_code.length).trim()
        }
      }

      setBillingFormData({
        companyName: user.companyName || '',
        companyAddress: user.companyAddress || '',
        selectedCountryCode,
        phoneNumber
      })
    }
  }, [user])

  // Handle form submission
  const handleSubmitBillingDetails = async (values, { setSubmitting }) => {
    try {
      // Combine country code with phone number for saving to database
      const fullPhoneNumber = values.selectedCountryCode + values.phoneNumber

      // Create the payload to send to your API
      const payload = {
        companyName: values.companyName,
        companyAddress: values.companyAddress,
        phoneNumber: fullPhoneNumber // The combined phone number with country code
      }

      // Call the API to update the user data
      const response = await updateCompanyInfo(payload).unwrap()
      if (response) {
        // Update the user data in Redux store
        dispatch(setUser({
          ...user,
          companyName: values.companyName,
          companyAddress: values.companyAddress,
          phoneNumber: fullPhoneNumber
        }))
        toast.success('Company info updated successfully!')
      }
    } catch (error) {
      toast.error('Failed to update company info!')
      throw error
    } finally {
      setSubmitting(false)
    }
  }

  const handleToggleDialog = () => {
    setIsDialogOpen(prev => !prev)
  }

  const handleOpencancellationPlans = () => {
    setCancellationPlansOpen(true)
    setIsDialogOpen(false)
  }

  const handleClosecancellationPlans = () => {
    setCancellationPlansOpen(false)
  }

  const handleFeedbackPopup = (action) => {
    if (action === 'open') {
      setFeedbackPopupOpen(true)
      handleClosecancellationPlans()
    } else if (action === 'close') {
      setFeedbackPopupOpen(false)
    }
  }

  const handleShowMore = () => {
    setDisplayCount(prev => prev + 4)
  }

  useEffect(() => {
    setWorkspaceId(workspace?._id)
  }, [workspace])

  const handelBillingCheckout = async () => {
    const { data: url } = await UpdatePayment()
    window.location.href = url
  }

  const handleDownloadAll = async () => {
    try {
      setIsDownloading(true)

      const failedDownloads = []

      // Process downloads sequentially to avoid overwhelming the browser
      for (const invoice of invoices) {
        try {
          const response = await fetch(invoice.pdfUrl)
          if (!response) {
            toast.error(`Failed to download invoice: ${invoice.pdfUrl}`)
          }

          const blob = await response.blob()
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = `invoice-${moment(invoice.createdAt).format('YYYY-MM-DD')}.pdf`

          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)

          window.URL.revokeObjectURL(link.href)
        } catch (err) {
          failedDownloads.push({
            invoice,
            errorMessage: err.message || 'Unknown error',
            errorType: err.name || 'Error'
          })
        }
      }

      // Show appropriate toast notification
      if (failedDownloads.length > 0) {
        toast.error(`Some invoices failed to download (${failedDownloads.length} failed)`)
      } else {
        toast.success('All invoices downloaded successfully')
      }
    } catch (error) {
      toast.error('Failed to download invoices!')
      throw error
    } finally {
      setIsDownloading(false)
    }
  }

  useEffect(() => {
    if (workspaceId) {
      refetchInvoice()
    }
  }, [workspaceId])

  return (
    <>
      <Box sx={{ padding: 0, backgroundColor: '#f9f9f9', mt: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6}>
            {/* Billing Details Card */}
            <Card sx={{ padding: { xs: '12px', sm: '16px' }, px: { xs: 2, sm: 4 }, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography
                  variant='h6'
                  sx={{
                    fontWeight: 'bold',
                    fontSize: { xs: '16px', sm: '20px' }
                  }}
                >
                  Billing Details
                </Typography>
              </Box>

              <Formik
                enableReinitialize
                initialValues={billingFormData}
                validationSchema={BillingDetailsSchema}
                onSubmit={handleSubmitBillingDetails}
              >
                {({ values, handleChange, isSubmitting, errors, touched }) => (
                  <Form>
                    {/* Company Name */}
                    <Box sx={{ mb: 2 }}>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', mb: 1, color: '#555' }}>
                        Company Name
                      </Typography>
                      <TextField
                        fullWidth
                        name='companyName'
                        disabled={!canEdit}
                        variant='outlined'
                        size='small'
                        value={values.companyName}
                        onChange={handleChange}
                        error={touched.companyName && Boolean(errors.companyName)}
                        helperText={touched.companyName && errors.companyName}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderRadius: '6px',
                              borderColor: '#e0e0e0'
                            }
                          }
                        }}
                      />
                    </Box>

                    {/* Company Address */}
                    <Box sx={{ mb: 2 }}>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', mb: 1, color: '#555' }}>
                        Company Address
                      </Typography>
                      <TextField
                        fullWidth
                        name='companyAddress'
                        disabled={!canEdit}
                        variant='outlined'
                        size='small'
                        value={values.companyAddress}
                        onChange={handleChange}
                        error={touched.companyAddress && Boolean(errors.companyAddress)}
                        helperText={touched.companyAddress && errors.companyAddress}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderRadius: '6px',
                              borderColor: '#e0e0e0'
                            }
                          }
                        }}
                      />
                    </Box>

                    {/* Email and Phone in a row */}
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
                      {/* Email Address */}
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 500, fontSize: '14px', mb: 1, color: '#555' }}>
                          Email Address
                        </Typography>
                        <TextField
                          fullWidth
                          disabled // Read only field
                          variant='outlined'
                          size='small'
                          value={user?.email || ''}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderRadius: '6px',
                                borderColor: '#e0e0e0'
                              }
                            }
                          }}
                        />
                      </Box>

                      {/* Phone Number */}
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 500, fontSize: '14px', mb: 1, color: '#555' }}>
                          Phone Number
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Box sx={{ display: 'flex' }}>
                            <FormControl
                              error={touched.selectedCountryCode && Boolean(errors.selectedCountryCode)}
                              sx={{ minWidth: '80px', mr: 1 }}
                            >
                              <Select
                                name='selectedCountryCode'
                                disabled={!canEdit}
                                value={values.selectedCountryCode}
                                onChange={handleChange}
                                size='small'
                                displayEmpty
                                renderValue={(value) => value || '+...'}
                                MenuProps={{
                                  PaperProps: {
                                    style: {
                                      maxHeight: 300,
                                      width: 200
                                    }
                                  }
                                }}
                                sx={{
                                  height: '40px',
                                  width: 'auto',
                                  '& .MuiSelect-select': {
                                    minWidth: '60px',
                                    paddingRight: '30px'
                                  },
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderRadius: '6px',
                                    borderColor: touched.selectedCountryCode && errors.selectedCountryCode ? 'error.main' : '#e0e0e0'
                                  }
                                }}
                              >
                                {countryPhoneCodes.map((country) => (
                                  <MenuItem key={country.code} value={country.dial_code}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                      <Typography component='span'>{country.dial_code}</Typography>
                                      <Typography component='span' sx={{ ml: 2, color: 'text.secondary', fontSize: '0.9em' }}>
                                        {country.name}
                                      </Typography>
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <TextField
                              fullWidth
                              name='phoneNumber'
                              disabled={!canEdit}
                              variant='outlined'
                              size='small'
                              value={values.phoneNumber}
                              onChange={handleChange}
                              error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  height: '40px',
                                  '& fieldset': {
                                    borderRadius: '6px',
                                    borderColor: '#e0e0e0'
                                  }
                                }
                              }}
                            />
                          </Box>
                          {touched.phoneNumber && errors.phoneNumber && (
                            <FormHelperText error sx={{ ml: '77px', mt: '4px' }}>
                              {errors.phoneNumber}
                            </FormHelperText>
                          )}
                        </Box>
                      </Box>

                    </Box>

                    {/* Save Button */}
                    {canEdit && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          type='submit'
                          variant='contained'
                          size='small'
                          disabled={isSubmitting || isUpdatingCompanyInfo || !canEdit}
                          sx={{
                            backgroundColor: theme.palette.primary.main,
                            '&:hover': {
                              backgroundColor: theme.palette.primary.dark
                            }
                          }}
                        >
                          {isSubmitting ? <CircularProgress size={24} /> : 'Save'}
                        </Button>
                      </Box>
                    )}
                  </Form>
                )}
              </Formik>

            </Card>

            {/* Payment Method Card */}
            <Card sx={{ padding: { xs: '12px', sm: '16px' }, px: { xs: 2, sm: 4 } }}>
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 'bold',
                  marginBottom: '8px',
                  fontSize: { xs: '16px', sm: '20px' }
                }}
              >
                Payment Method
              </Typography>
              <Typography sx={{ color: '#6b6b6b', marginBottom: '16px' }}>
                This information is general and applies across the entire platform.
              </Typography>

              {isCardLoading
                ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100px'
                    }}
                  >
                    <CircularProgress />
                  </Box>
                  )
                : !latestPaymentMethod
                    ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'end' }}>
                        <Typography
                          sx={{
                            display: 'flex',
                            justifyContent: 'end',
                            textAlign: 'end',
                            fontWeight: '700',
                            fontSize: '14px'
                          }}
                        >
                          No payment method found
                        </Typography>
                        <Box>
                          <Button
                            variant='contained'
                            sx={{
                              mt: 1,
                              backgroundColor: `${theme.palette.primary.main}`,
                              color: `${theme.palette.primary.contrastText}`,
                              '&:hover': {
                                backgroundColor: `${theme.palette.primary.main}`
                              }
                            }}
                            onClick={handelBillingCheckout}
                          >
                            Add Payment Method
                          </Button>
                        </Box>
                      </Box>
                      )
                    : (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          justifyContent: 'space-between',
                          backgroundColor: '#fff',
                          padding: '16px',
                          borderRadius: '8px',
                          border: '1px solid #ddd',
                          gap: { xs: 2, sm: 0 }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <img
                            src='https://upload.wikimedia.org/wikipedia/commons/a/a4/Mastercard_2019_logo.svg'
                            alt='Mastercard'
                            style={{ width: '40px', marginRight: '16px' }}
                          />
                          <Box>
                            <Box sx={{ display: 'flex' }}>
                              <Typography sx={{ fontWeight: 'bold' }}>
                                {latestPaymentMethod?.brand} ending in {latestPaymentMethod?.last4}
                              </Typography>
                              {isCardExpired(
                                latestPaymentMethod?.exp_month,
                                latestPaymentMethod?.exp_year
                              ) && (
                                <Chip
                                  label='Expired'
                                  sx={{
                                    backgroundColor: '#ffdede',
                                    color: '#d8000c',
                                    fontWeight: 'bold',
                                    ml: 2
                                  }}
                                />
                              )}
                            </Box>
                            <Typography sx={{ fontSize: '14px', color: '#6b6b6b' }}>
                              {latestPaymentMethod?.name} · Exp. date {latestPaymentMethod?.exp_month}/
                              {latestPaymentMethod?.exp_year}
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            justifyContent: { xs: 'center', sm: 'flex-end' },
                            width: { xs: '100%', sm: 'auto' }
                          }}
                        >
                          <Button
                            variant='text'
                            sx={{
                              color: `${theme.palette.primary.main}`,
                              fontWeight: 600,
                              fontSize: '14px'
                            }}
                            onClick={handelBillingCheckout}
                          >
                            Update
                          </Button>
                        </Box>
                      </Box>
                      )}
            </Card>

            {/* Subscription Card */}
            <Card
              sx={{
                padding: { xs: '12px', sm: '16px' },
                mt: 2,
                px: { xs: 2, sm: 4 }
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: '16px', marginBottom: '2px' }}>
                Manage Success.ai subscription
              </Typography>
              <Typography
                sx={{ color: '#8181B0', marginBottom: '16px', fontWeight: 400, fontSize: '13px' }}
              >
                Update your Success.ai plan settings or explore other options below.
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: '16px',
                  justifyContent: 'space-between',
                  flexDirection: { xs: 'column', sm: 'row' }
                }}
              >
                <Button
                  variant='outlined'
                  disabled={!canEdit}
                  sx={{
                    borderColor: '#E4E4E5',
                    color: '#FF4545',
                    fontWeight: 'bold'
                  }}
                  onClick={handleToggleDialog}
                >
                  Cancel my plan
                </Button>
                <Button
                  variant='contained'
                  disabled={!canEdit}
                  sx={{
                    backgroundColor: `${theme.palette.primary.main}`,
                    color: '#fff',
                    fontWeight: 'bold',
                    '&:hover': { backgroundColor: `${theme.palette.primary.main}` }
                  }}
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() => setValue(4)}
                >
                  Upgrade my plan
                </Button>
              </Box>
            </Card>
          </Grid>
          <UpgradePlanPopup
            isOpen={isDialogOpen}
            onClose={handleToggleDialog}
            handleOpencancellationPlans={handleOpencancellationPlans}
            workspaceId={workspaceId}
          />

          <CancellationPlans
            cancellationPlansOpen={cancellationPlansOpen}
            handleClosecancellationPlans={handleClosecancellationPlans}
            handleFeedbackPopup={handleFeedbackPopup}
          />

          <FeedbackPopup isOpen={feedbackPopupOpen} onClose={() => handleFeedbackPopup('close')} />

          {/* Invoices (35) Card */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                marginBottom: '24px',
                padding: { xs: '12px', sm: '16px' },
                height: '100%',
                px: { xs: 2, sm: 4 }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 2, sm: 0 }
                }}
              >
                <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                  Invoices ({invoices?.length || 0})
                </Typography>
                <Button
                  variant='outlined'
                  onClick={handleDownloadAll}
                  disabled={isDownloading || !invoices?.length || !canEdit}
                  startIcon={
                    isDownloading ? <CircularProgress size={20} /> : <FileDownloadIcon />
                  }
                  sx={{
                    fontWeight: 'bold',
                    color: 'black',
                    border: '1px solid #E4E4E5',
                    '&:disabled': {
                      color: 'grey.400',
                      borderColor: 'grey.300'
                    }
                  }}
                >
                  {isDownloading ? 'Downloading...' : `Download all (${invoices?.length || 0})`}
                </Button>
              </Box>
              <Box sx={{ marginTop: '16px' }}>
                {isLoading
                  ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '200px' // Prevents layout shift
                      }}
                    >
                      <CircularProgress sx={{ color: '#3F4FF8' }} />{' '}
                      {/* Matches your theme color */}
                    </Box>
                    )
                  : (
                    <>
                      {visibleInvoices.map((invoice, index) => (
                        <Box key={index}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '8px 0'
                            }}
                          >
                            <Box sx={{ display: 'flex' }}>
                              <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                                <span style={{ marginRight: '8px', color: '#8181B0' }}>📄</span>{' '}
                                {moment(invoice.createdAt).format('MM/DD/YYYY')}
                              </Typography>
                              <Chip
                                label={invoice?.status === 'paid' ? 'Paid' : 'Not paid'}
                                sx={{
                                  backgroundColor:
                                    invoice?.status === 'paid' ? '#e0f6e0' : '#FFDFDF', // Green for "Paid", Red for "Not paid"
                                  color: invoice?.status === 'paid' ? '#008a00' : '#d8000c', // Text color matching the background
                                  fontWeight: 'bold',
                                  ml: 2
                                }}
                              />
                            </Box>
                            <Box>
                              <Button
                                variant='text'
                                disabled={!canEdit}
                                sx={{ color: '#3F4FF8', fontWeight: 600, fontSize: '14px' }}
                              >
                                <Link to={invoice.pdfUrl} target='_blank'>
                                  Download
                                </Link>
                              </Button>
                            </Box>
                          </Box>
                          {/* Add Divider only if it's not the last item */}
                          {index < visibleInvoices.length - 1 && (
                            <Divider
                              sx={{
                                margin: '8px 0',
                                borderColor: '#e0e0e0',
                                borderWidth: '1px',
                                width: '100%',
                                position: 'relative',
                                '::before, ::after': {
                                  borderColor: '#e0e0e0'
                                },
                                color: '#555',
                                fontWeight: 500
                              }}
                            />
                          )}
                        </Box>
                      ))}
                      {hasMoreInvoices && (
                        <Typography
                          onClick={handleShowMore}
                          sx={{
                            color: '#3F4FF8',
                            fontWeight: 600,
                            cursor: 'pointer',
                            textAlign: 'center',
                            fontSize: '14px'
                          }}
                        >
                          Show more ({remainingCount})
                        </Typography>
                      )}
                    </>
                    )}
              </Box>
            </Card>
          </Grid>
        </Grid>

      </Box>
    </>
  )
}

export default BillingUpdatedPage
